import { ChevronDown } from "lucide-react";
import { Fragment, useState, type ReactNode } from "react";

import { ListRow } from "@/components/ui/list-card";
import { cn } from "@/lib/utils";

import { groupTopics, topicFileTitle } from "./classicModel";

type Topic = { title: string; group?: string | null };

export type TopicView = {
  media: ReactNode;
  control?: ReactNode;
  description?: ReactNode;
};

type Props<T extends Topic> = {
  rows: T[];
  /** One topic: a case topic, or a file under an open heading. */
  topic: (row: T) => TopicView;
  /** A heading's row, from the files under it. */
  heading: (rows: T[]) => TopicView;
};

/**
 * Research topics for a ListCard: case topics one row each, per-file topics
 * folded under their heading ("Journal Impact · 9 files"), opened on click.
 */
export function ResearchTopicRows<T extends Topic>({
  rows,
  topic,
  heading
}: Props<T>) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <>
      {groupTopics(rows).map((group) => {
        if (!group.files) {
          const view = topic(group.rows[0]);
          return (
            <ListRow
              key={group.key}
              media={<span className="flex w-3.5">{view.media}</span>}
              title={group.title}
              description={view.description}
              control={view.control}
            />
          );
        }
        const view = heading(group.rows);
        const expanded = open.has(group.key);
        return (
          <Fragment key={group.key}>
            <ListRow
              interactive
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              onClick={() => toggle(group.key)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggle(group.key);
                }
              }}
              media={<span className="flex w-3.5">{view.media}</span>}
              title={group.title}
              description={view.description}
              control={
                <>
                  {view.control}
                  <ChevronDown
                    className={cn(
                      "size-3.5 text-fg-3 transition-transform duration-100",
                      expanded && "rotate-180"
                    )}
                  />
                </>
              }
            />
            {expanded
              ? group.rows.map((row, index) => {
                  const file = topic(row);
                  return (
                    <ListRow
                      key={`${group.key}-${index}`}
                      className="py-2 pl-11"
                      media={<span className="flex w-3.5">{file.media}</span>}
                      title={topicFileTitle(row)}
                      description={
                        file.description ? (
                          <span className="text-sm text-fg-3">
                            {file.description}
                          </span>
                        ) : undefined
                      }
                      control={file.control}
                    />
                  );
                })
              : null}
          </Fragment>
        );
      })}
    </>
  );
}
